import { useEffect, useRef } from "react";

type CountUpProps = {
  /** Wartość docelowa, np. 10000 */
  to: number;
  /** Tekst przed liczbą, np. "+ " */
  prefix?: string;
  /** Tekst po liczbie, np. "h" */
  suffix?: string;
  /** Czas trwania animacji w ms */
  duration?: number;
  className?: string;
};

const format = (n: number) => n.toLocaleString("pl-PL").replace(/\u00a0/g, " ");

const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

export function CountUp({ to, prefix = "", suffix = "", duration = 1600, className }: CountUpProps) {
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduced) {
      return;
    }

    let frame = 0;
    let start = 0;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        observer.disconnect();
        node.textContent = `${prefix}${format(0)}${suffix}`;

        const step = (now: number) => {
          if (!start) start = now;
          const progress = Math.min((now - start) / duration, 1);
          node.textContent = `${prefix}${format(Math.round(easeOut(progress) * to))}${suffix}`;
          if (progress < 1) {
            frame = requestAnimationFrame(step);
          } else {
            node.textContent = `${prefix}${format(to)}${suffix}`;
          }
        };
        frame = requestAnimationFrame(step);
      },
      { threshold: 0.35 },
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [to, prefix, suffix, duration]);

  return (
    <span ref={ref} className={className} aria-label={`${prefix}${format(to)}${suffix}`}>
      {prefix}
      {format(to)}
      {suffix}
    </span>
  );
}
